import { FilterUpdateInput } from '@vivport/backend/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createFilter,
  deleteFilter,
  getFilter,
  listFilters,
  listTabFilters,
  reorderFilters,
  updateFilter
} from '../api/filter-api';

export const filterKeys = {
  all: ['filters'] as const,
  lists: () => [...filterKeys.all, 'list'] as const,
  list: () => [...filterKeys.lists()] as const,
  tabs: (memberId: number, hubId: number) => [...filterKeys.all, 'tabs', memberId, hubId] as const,
  details: () => [...filterKeys.all, 'detail'] as const,
  detail: (id: number) => [...filterKeys.details(), id] as const,
};

export const useTabFilters = (memberId: number | undefined, hubId: number | undefined) => {
  const enabled = memberId !== undefined && memberId > 0 && hubId !== undefined && hubId > 0;
  return useQuery({
    queryKey: enabled ? filterKeys.tabs(memberId!, hubId!) : [...filterKeys.all, 'tabs', 'disabled'] as const,
    queryFn: () => listTabFilters(),
    enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useFilterList = () => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: filterKeys.list(),
    queryFn: async (): Promise<{ ids: number[] }> => {
      const filters = await listFilters();

      filters.forEach((item) => {
        queryClient.setQueryData(filterKeys.detail(item.id), item);
      });

      return { ids: filters.map(item => item.id) };
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useFilterDetail = (id: number) => {
  return useQuery({
    queryKey: filterKeys.detail(id),
    queryFn: () => getFilter(id),
    enabled: id > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useFilterCreate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createFilter,
    onSuccess: (created) => {
      queryClient.setQueryData(filterKeys.detail(created.id), created);
      queryClient.invalidateQueries({ queryKey: filterKeys.lists() });
      queryClient.invalidateQueries({ queryKey: [...filterKeys.all, 'tabs'] });
    },
  });
};

export const useFilterUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: FilterUpdateInput }) => updateFilter(id, data),
    onSuccess: (updated) => {
      queryClient.setQueryData(filterKeys.detail(updated.id), updated);
      queryClient.invalidateQueries({ queryKey: filterKeys.lists() });
      queryClient.invalidateQueries({ queryKey: [...filterKeys.all, 'tabs'] });
    },
  });
};

export const useFilterDelete = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteFilter,
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: filterKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: filterKeys.lists() });
      queryClient.invalidateQueries({ queryKey: [...filterKeys.all, 'tabs'] });
    },
  });
};

export const useFilterReorder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: reorderFilters,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: filterKeys.lists() });
      queryClient.invalidateQueries({ queryKey: [...filterKeys.all, 'tabs'] });
    },
  });
};
